import { isTaskRepeating } from '@frontend/api-client';
import type { RatingValue, Task } from '@frontend/api-types';
import type { TaskMemberConfirmationStatusOption, TaskNotDoneReason } from '@frontend/constants';
import type { SetNonNullable, StrictOmit } from '@frontend/duck-tape';
import { P, match } from '@frontend/duck-tape';
import { useMemo } from '@frontend/react';
import type { ApiClientHook, HandleMutationFn } from '@frontend/react-domain/utils/types';
import { useSetState } from '@mantine/hooks';

export type CloseTaskStep = 'incompleteReason' | 'recurringWarning' | 'selectConfirmationStatus' | 'taskReview';

export type CloseTaskState = {
  confirmationStatus: MaybeUndefined<TaskMemberConfirmationStatusOption>;
  feedback: string;
  notDoneReason: MaybeUndefined<TaskNotDoneReason>;
  rating: MaybeUndefined<RatingValue>;
  step: CloseTaskStep;
};

type UseCloseTaskFlowProps = {
  closeDialog: () => void;
  closeTask: ReturnType<ApiClientHook['useCloseTaskMutation']>[0];
  confirmDoneStatus: ReturnType<ApiClientHook['useConfirmDoneMutation']>[0];
  createReview: ReturnType<ApiClientHook['useCreateTaskReviewMutation']>[0];
  handleMutation: HandleMutationFn;
  isCloseTaskLoading: boolean;
  isConfirmDoneLoading: boolean;
  isCreateTaskReviewLoading: boolean;
  isRecurrencesRevampedEnabled?: boolean;
  showToast: (props: { backgroundColor: 'blue' | 'green'; title: string }) => void;
  task: StrictOmit<Task, 'zendeskId'>;
};

export const useCloseTaskFlow = ({
  closeDialog,
  closeTask,
  confirmDoneStatus,
  createReview,
  handleMutation,
  isCloseTaskLoading,
  isConfirmDoneLoading,
  isCreateTaskReviewLoading,
  isRecurrencesRevampedEnabled = false,
  showToast,
  task,
}: UseCloseTaskFlowProps) => {
  const showRecurringWarning = isRecurrencesRevampedEnabled && isTaskRepeating(task);

  const initialState: CloseTaskState = {
    confirmationStatus: undefined,
    feedback: '',
    notDoneReason: undefined,
    rating: undefined,
    step: showRecurringWarning ? 'recurringWarning' : 'selectConfirmationStatus',
  };

  const [state, setState] = useSetState<CloseTaskState>(initialState);

  const resetState = () => {
    setState(initialState);
  };

  const closeAndResetState = () => {
    closeDialog();
    resetState();
  };

  const isSubmitLoading = useMemo(
    () => isCloseTaskLoading || isCreateTaskReviewLoading || isConfirmDoneLoading,
    [isCloseTaskLoading, isCreateTaskReviewLoading, isConfirmDoneLoading],
  );

  const submitCloseTask = async (submitState: SetNonNullable<CloseTaskState, 'confirmationStatus' | 'rating'>) =>
    handleMutation({
      mutation: async () => closeTask({ id: task.id }),
      onError: closeAndResetState,
      onSuccess: async () =>
        handleMutation({
          mutation: async () =>
            confirmDoneStatus({
              memberConfirmationStatus: submitState.confirmationStatus!,
              taskId: task.id,
            }),
          onSuccess: async () =>
            handleMutation({
              mutation: async () =>
                createReview({
                  cannedResponses: submitState.notDoneReason ? [submitState.notDoneReason] : [],
                  description: submitState.feedback,
                  rating: submitState.rating,
                  task: task.id,
                }),
              onError: closeAndResetState,
              onSuccess: () => {
                showToast(
                  match<typeof submitState.confirmationStatus>(submitState.confirmationStatus)
                    .with(
                      'done',
                      () =>
                        ({
                          backgroundColor: 'green',
                          title: 'Marked as done 🎉',
                        }) as const,
                    )
                    .with(
                      'not_done',
                      () =>
                        ({
                          backgroundColor: 'blue',
                          title: 'Task closed',
                        }) as const,
                    )
                    .exhaustive(),
                );
                closeAndResetState();
              },
              showErrorToast: false,
              showSuccessToast: false,
            }),
          showErrorToast: false,
          showSuccessToast: false,
        }),
      showErrorToast: false,
      showSuccessToast: false,
    });

  const title = match(state.step)
    .with('recurringWarning', () => 'Close Task')
    .with('selectConfirmationStatus', () => 'Close Task')
    .with('incompleteReason', () =>
      match(state.confirmationStatus)
        .with('done', () => 'Done')
        .with('not_done', () => 'Close Incomplete')
        .with(P.nullish, () => '')
        .exhaustive(),
    )
    .with('taskReview', () =>
      match(state.confirmationStatus)
        .with('done', () => 'How was the task?')
        .with('not_done', () => 'Close incomplete')
        .with(P.nullish, () => '')
        .exhaustive(),
    )
    .exhaustive();

  const canGoBack = match(state.step)
    .with('recurringWarning', () => false)
    .with('selectConfirmationStatus', () => showRecurringWarning)
    .with('incompleteReason', () => true)
    .with('taskReview', () => true)
    .exhaustive();

  const onAcknowledgeRecurringWarning = () => {
    setState({ step: 'selectConfirmationStatus' });
  };

  const handleBackClick = () => {
    match(state.step)
      .with('taskReview', () =>
        setState(
          state.confirmationStatus === 'done'
            ? {
                confirmationStatus: undefined,
                feedback: '',
                notDoneReason: undefined,
                rating: undefined,
                step: 'selectConfirmationStatus',
              }
            : { step: 'incompleteReason' },
        ),
      )
      .with('incompleteReason', () =>
        setState({
          confirmationStatus: undefined,
          feedback: '',
          notDoneReason: undefined,
          rating: undefined,
          step: 'selectConfirmationStatus',
        }),
      )
      .with('selectConfirmationStatus', () => {
        if (showRecurringWarning) {
          setState({ step: 'recurringWarning' });
        }
      })
      .with('recurringWarning', () => {})
      .exhaustive();
  };

  const handleSubmitClick = async () => {
    if (state.confirmationStatus && state.rating) {
      await submitCloseTask({
        ...state,
        confirmationStatus: state.confirmationStatus!,
        rating: state.rating!,
      });
    }
  };

  const isSubmitDisabled = !state.confirmationStatus || !state.rating;

  const onSelectConfirmationStatus = (confirmationStatus: TaskMemberConfirmationStatusOption) => {
    setState({
      confirmationStatus,
      notDoneReason: confirmationStatus === 'done' ? undefined : state.notDoneReason,
      step: confirmationStatus === 'done' ? 'taskReview' : 'incompleteReason',
    });
  };

  const onSelectNotDoneReason = (notDoneReason: TaskNotDoneReason) => {
    setState({ notDoneReason, step: 'taskReview' });
  };

  const onSkipNotDoneReason = () => {
    setState({ notDoneReason: undefined, step: 'taskReview' });
  };

  const onUpdateRating = (rating: RatingValue) => {
    setState({ rating });
  };

  const onUpdateFeedback = (feedback: string) => {
    setState({ feedback });
  };

  return {
    canGoBack,
    closeAndResetState,
    handleBackClick,
    handleSubmitClick,
    isSubmitDisabled,
    isSubmitLoading,
    onAcknowledgeRecurringWarning,
    onSelectConfirmationStatus,
    onSelectNotDoneReason,
    onSkipNotDoneReason,
    onUpdateFeedback,
    onUpdateRating,
    resetState,
    showRecurringWarning,
    state,
    title,
  };
};
